import type { TrackingConfig } from '../api/config';
import { marketingLanguageForPath } from '../components/landing/LandingShell';
import { onConsentChange, readConsent, type Consent } from './consent';

/**
 * Google Analytics and Google Ads, on the visitor's terms and on the public site only.
 *
 * Three rules, in the order they bind:
 *
 * 1. **Nothing loads without consent.** Not "loads with consent denied" — nothing is requested
 *    from Google at all until the visitor has said yes to at least one purpose. When they have,
 *    their answer is passed to the tag through Consent Mode v2, so a yes to analytics is not
 *    treated as a yes to advertising.
 *
 * 2. **Nothing runs inside an account.** The signed-in app shows national identification numbers,
 *    income and tax figures. A third-party script in that page can read all of it, and "it is
 *    Google, it will not" is a sentence about today's script and today's settings in somebody's
 *    Google Ads account. So the tag is loaded on the marketing site and the registration page only;
 *    and because a single-page app never unloads a script, crossing into the app from a page that
 *    loaded one is done with a real page load, which does.
 *
 * 3. **Identifiers come from configuration.** `Tracking` in appsettings.json; blank means off,
 *    and with nothing configured there is no banner either, because there is nothing to agree to.
 */

export type TrackedEvent = 'sign_up' | 'calculator_used';

/** Where a path stands in rule 2. */
export type Area = 'tracked' | 'neutral' | 'private';

/** Anonymous pages with nothing to measure: reached from an email or by somebody who already has an account. */
const NEUTRAL_PATHS = ['/login', '/confirm-email', '/forgot-password', '/reset-password', '/unsubscribe'];

export function areaOf(pathname: string): Area {
  if (marketingLanguageForPath(pathname) !== null || pathname === '/register') {
    return 'tracked';
  }

  return NEUTRAL_PATHS.includes(pathname) ? 'neutral' : 'private';
}

type GtagArguments = [command: string, ...rest: unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

let config: TrackingConfig | null = null;
let loaded: { analytics: boolean; marketing: boolean } | null = null;

/**
 * The two ways this module replaces the document. Behind an object so a test can watch them:
 * jsdom's own `location` cannot be spied on, and "did it reload" is exactly what needs asserting.
 */
export const pageLoads = {
  reload: () => window.location.reload(),
  replace: (url: string) => window.location.replace(url),
};

function gtag(..._args: GtagArguments): void {
  // The tag reads the `arguments` object itself off the queue; an array is silently ignored.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments);
}

function signals(consent: Consent) {
  return {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  };
}

/** What this visitor's answer allows, given what is configured. */
function wanted(consent: Consent | null) {
  return {
    analytics: Boolean(consent?.analytics && config?.googleAnalyticsId),
    marketing: Boolean(consent?.marketing && config?.googleAdsId),
  };
}

function load(consent: Consent): void {
  const want = wanted(consent);
  const firstId = want.analytics ? config?.googleAnalyticsId : config?.googleAdsId;

  if (!firstId || (!want.analytics && !want.marketing)) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];

  // Denied first, then the visitor's actual answer: the order Consent Mode expects, and it means
  // a purpose they did not agree to stays denied even though the library is on the page.
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('consent', 'update', signals(consent));
  gtag('js', new Date());

  if (want.analytics) {
    // Page views are sent by hand: this is a single-page app, and the automatic one would count
    // the first page and none of the navigation after it.
    gtag('config', config!.googleAnalyticsId!, { send_page_view: false });
  }

  if (want.marketing) {
    gtag('config', config!.googleAdsId!);
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstId)}`;
  document.head.appendChild(script);

  loaded = want;
}

/** Google's own cookies, removed when consent is taken back. Best effort: both domain spellings. */
function forgetGoogleCookies(): void {
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => /^(_ga|_gid|_gat|_gcl_|_gac_)/.test(name));

  const host = window.location.hostname;
  const parent = host.split('.').slice(-2).join('.');

  for (const name of names) {
    for (const domain of ['', `; domain=${host}`, `; domain=.${parent}`]) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
}

function sync(consent: Consent | null): void {
  if (!config) {
    return;
  }

  if (loaded) {
    const want = wanted(consent);

    if ((loaded.analytics && !want.analytics) || (loaded.marketing && !want.marketing)) {
      // Taken back. Telling the tag is not enough — it is still in memory with whatever it has
      // read — so the cookies go and the page is loaded again without it.
      if (consent) gtag('consent', 'update', signals(consent));
      forgetGoogleCookies();
      pageLoads.reload();
      return;
    }

    if (consent && (want.analytics !== loaded.analytics || want.marketing !== loaded.marketing)) {
      // Something was added. A fresh load configures exactly what is now allowed.
      pageLoads.reload();
    }

    return;
  }

  if (consent && areaOf(window.location.pathname) === 'tracked') {
    load(consent);
    persistAcquisition(consent);
    trackPageView();
  }
}

/** Called once the public configuration has arrived. Null switches everything off. */
export function configureTracking(next: TrackingConfig | null): void {
  config = next;
  sync(readConsent());
}

onConsentChange((consent) => sync(consent));

/** True while Google's script is in this document. */
export function isTrackingLoaded(): boolean {
  return loaded !== null;
}

/**
 * Called on every navigation. Returns true when it has started a full page load, so the caller
 * can stop rendering into a document that is about to be replaced.
 */
export function onRouteChange(pathname: string): boolean {
  const area = areaOf(pathname);

  if (loaded && area === 'private') {
    // Rule 2. A reload of the URL the router has already moved to: same place, clean document.
    pageLoads.replace(window.location.href);
    return true;
  }

  if (!loaded) {
    // Arrived on a tracked page from one that was not — a sign-out back to the landing page.
    sync(readConsent());
    return false;
  }

  if (area === 'tracked') {
    trackPageView();
  }

  return false;
}

let lastPageView: string | null = null;

function trackPageView(): void {
  if (!loaded?.analytics) {
    return;
  }

  // Loading the tag and the router's first effect both arrive here for the landing page. One view.
  const here = window.location.pathname + window.location.search;
  if (here === lastPageView) {
    return;
  }
  lastPageView = here;

  // After the page's own effects, so the title is the new page's and not the one being left.
  window.setTimeout(() => {
    gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  }, 0);
}

/**
 * Reports something worth counting. To Analytics as an event; to Ads as a conversion, when that
 * event has a conversion label configured. A no-op without consent, which is the usual case.
 */
export function track(event: TrackedEvent, params: Record<string, unknown> = {}): void {
  if (!loaded || !config) {
    return;
  }

  if (loaded.analytics) {
    gtag('event', event, params);
  }

  const label = config.conversions[event];

  if (loaded.marketing && config.googleAdsId && label) {
    gtag('event', 'conversion', { send_to: `${config.googleAdsId}/${label}` });
  }
}

// ---------------------------------------------------------------------------------------------
// Where a sign-up came from
// ---------------------------------------------------------------------------------------------

export interface Acquisition {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  gclid: string | null;
  landingPath: string | null;
  referrerHost: string | null;
  landedAt: string;
}

const ACQUISITION_KEY = 'danak.acquisition';

/** First-touch: kept for ninety days, which comfortably spans "saw an ad" to "opened an account". */
const ACQUISITION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

function snapshot(): Acquisition {
  const query = new URLSearchParams(window.location.search);
  let referrerHost: string | null = null;

  try {
    const host = document.referrer ? new URL(document.referrer).hostname : '';
    referrerHost = host && host !== window.location.hostname ? host : null;
  } catch {
    referrerHost = null;
  }

  return {
    utmSource: query.get('utm_source'),
    utmMedium: query.get('utm_medium'),
    utmCampaign: query.get('utm_campaign'),
    utmTerm: query.get('utm_term'),
    utmContent: query.get('utm_content'),
    gclid: query.get('gclid'),
    landingPath: window.location.pathname,
    referrerHost,
    landedAt: new Date().toISOString(),
  };
}

/*
 * Taken the moment the application starts and held in memory only. The banner is answered a few
 * seconds — and often a click or two — after landing, by which time the address bar no longer
 * carries the campaign parameters. Holding them in a variable stores nothing on the device; they
 * are written down only if and when the visitor agrees to analytics.
 */
const landing: Acquisition | null = typeof window === 'undefined' ? null : snapshot();

function persistAcquisition(consent: Consent): void {
  if (!consent.analytics || !landing || readAcquisition()) {
    return;
  }

  try {
    localStorage.setItem(ACQUISITION_KEY, JSON.stringify(landing));
  } catch {
    // Without storage it lives as long as this tab does, which still covers "land, then register".
  }
}

/** What to send with a registration: the first touch, when the visitor allowed it to be kept. */
export function readAcquisition(): Acquisition | null {
  if (!readConsent()?.analytics) {
    return null;
  }

  try {
    const raw = localStorage.getItem(ACQUISITION_KEY);

    if (raw) {
      const stored = JSON.parse(raw) as Acquisition;

      if (Date.now() - Date.parse(stored.landedAt) <= ACQUISITION_MAX_AGE_MS) {
        return stored;
      }
    }
  } catch {
    // Fall through to this visit's own landing.
  }

  return null;
}

/** The acquisition to attach to a sign-up now: the stored first touch, or failing that this visit's. */
export function acquisitionForSignUp(): Acquisition | null {
  if (!readConsent()?.analytics) {
    return null;
  }

  return readAcquisition() ?? landing;
}

export function clearAcquisition(): void {
  try {
    localStorage.removeItem(ACQUISITION_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** For tests: the module keeps state that a test must be able to start from scratch. */
export function resetTrackingForTests(): void {
  config = null;
  loaded = null;
  lastPageView = null;
}
